import React, { useEffect, useState } from 'react';

import { Button } from '@/design-system/button';
import { FormModal } from '@/design-system/form-modal';
import { Textarea } from '@/design-system/textarea';
import { toast } from '@/design-system/toaster';
import { Invoice, invoicesAPI } from '@/services/api/invoices';

// Statuses that can be handed to the client for confirmation. Mirrors the
// backend guard in submit-for-confirm.usecase.ts — keep the two in sync.
const SUBMITTABLE_STATUSES: Invoice['status'][] = [
  'RECEIVED_AT_HUB',
  'RECEIVED_BY_RIDER',
];

interface SubmitForConfirmModalProps {
  isOpen: boolean;
  invoice: Invoice | null;
  onClose: () => void;
  onUpdated: (invoice: Invoice) => void;
}

const SubmitForConfirmModal: React.FC<SubmitForConfirmModalProps> = ({
  isOpen,
  invoice,
  onClose,
  onUpdated,
}) => {
  const [note, setNote] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setNote('');
      setIsSubmitting(false);
    }
  }, [isOpen]);

  if (!isOpen || !invoice) return null;

  const canSubmit = SUBMITTABLE_STATUSES.includes(invoice.status);

  const handleSubmit = async () => {
    if (isSubmitting || !canSubmit) return;
    setIsSubmitting(true);
    try {
      const updated = await invoicesAPI.submitForConfirm(
        invoice.id,
        note.trim() || null
      );
      toast.success('Submitted for confirmation', {
        description: `${updated.invoiceNumber} is now awaiting client confirmation.`,
      });
      onUpdated(updated);
      onClose();
    } catch (err) {
      toast.error('Could not submit invoice', {
        description: err instanceof Error ? err.message : 'Unknown error',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <FormModal
      open={isOpen}
      onClose={onClose}
      icon={<i className="ri-send-plane-line" />}
      iconTone="info"
      title="Submit for client confirmation"
      subtitle={`${invoice.invoiceNumber} · ${invoice.client.name}`}
      footer={
        <div className="flex items-center justify-end gap-2">
          <Button tone="neutral" style="stroke" onClick={onClose}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={isSubmitting || !canSubmit}
            loading={isSubmitting}
          >
            {isSubmitting ? 'Submitting…' : 'Submit for confirm'}
          </Button>
        </div>
      }
    >
      <div className="space-y-3">
        {!canSubmit && (
          <div className="flex items-start gap-2 rounded-gsm border border-error-light bg-error-lighter px-3 py-2 text-sm text-error-base">
            <i className="ri-error-warning-line mt-0.5" />
            <span>
              This invoice can’t be submitted for confirmation from its current
              status. Only received invoices are eligible.
            </span>
          </div>
        )}
        <p className="text-sm text-text-sub-600">
          Mark that the physical surat jalan has been handed to the client and
          is now awaiting their confirmation. This moves the invoice out of the
          received backlog and into the confirmation queue.
        </p>
        <div>
          <label
            htmlFor="submit-note"
            className="mb-2 block text-[10px] font-bold uppercase tracking-[0.18em] text-text-sub-600"
          >
            Note (optional)
          </label>
          <Textarea
            id="submit-note"
            rows={3}
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="e.g. handed to Pak Budi at 14:00"
          />
        </div>
      </div>
    </FormModal>
  );
};

export default SubmitForConfirmModal;
